import React, { useMemo } from 'react';
import { getClipboardText } from 'features/claim/clipboard';
import { NEW_PASTE_URL } from 'config/data-sources';
import { SlidingButtonLabel } from 'shared/components/SlidingButtonLabel';

interface CreateProfileButtonProps {
  /** Whether the button should be disabled. */
  createDisabled: boolean;
  /** Current display name for the profile. */
  displayName: string;
  /** Array of enabled badge IDs. */
  enabledBadges: string[];
  /** Callback fired after the submission code is generated and copied. */
  onSubmissionTriggered?: (submissionCode: string) => void;
  /** Called when mouse enters/leaves the button text (for validation hints). */
  setCreateTouched: (touched: boolean) => void;
  /** Optional tooltip / summary id for aria-describedby. */
  ariaDescribedBy?: string;
  /**
   * When set, runs instead of the default copy + open form flow
   * (e.g. navigate to a summary/confirm step first).
   */
  onActivate?: () => void;
  /** Override the default “Create with N Badges” label. */
  label?: string;
}

/**
 * Create-profile button that copies the submission code to clipboard
 * and opens the submission form.
 */
export const CreateProfileButton: React.FC<CreateProfileButtonProps> = ({
  createDisabled,
  displayName,
  enabledBadges,
  onSubmissionTriggered,
  setCreateTouched,
  ariaDescribedBy,
  onActivate,
  label: labelOverride,
}) => {
  const count = enabledBadges.length;
  const label = useMemo(() => {
    if (labelOverride) return labelOverride;
    if (count <= 0) return 'Create profile';
    return `Create with ${count} Badge${count === 1 ? '' : 's'}`;
  }, [count, labelOverride]);

  return (
    <button
      className={`main-button ${createDisabled ? 'disabled faded-out-text' : ''} `}
      onClick={() => {
        if (createDisabled) {
          setCreateTouched(true);
          return;
        }
        if (onActivate) {
          onActivate();
          return;
        }
        navigator.clipboard.writeText(getClipboardText(displayName, enabledBadges));
        window.open(NEW_PASTE_URL, '_blank');
        onSubmissionTriggered?.(getClipboardText(displayName, enabledBadges));
      }}
      disabled={false}
      aria-disabled={createDisabled}
      aria-label={label}
      aria-describedby={ariaDescribedBy}
      onMouseEnter={() => setCreateTouched(true)}
      onMouseLeave={() => setCreateTouched(false)}
    >
      <p>
        <SlidingButtonLabel text={label} />
      </p>
    </button>
  );
};
